import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { getUpcomingFlights, createFlight } from '../services/api';
import { useAuth } from '../context/AuthContext';
import checkmarkIcon from '../assets/icons/checkmark.svg';
import './FlyAircraftModal.css';

const FLIGHT_NUMBER_PATTERN = /^(?:(?:WZZ|WMT|WUK|W6|W4|W9)[A-Z0-9]{1,5}|\d{1,5})$/;
const ICAO_PATTERN = /^[A-Z]{4}$/;
const CREATED_FLIGHT_PAYLOADS_KEY = 'etlb_created_flight_payloads';

const saveCreatedFlightPayload = flight => {
  if (!flight?.id) return;
  try {
    const cache = JSON.parse(localStorage.getItem(CREATED_FLIGHT_PAYLOADS_KEY) || '{}');
    cache[String(flight.id)] = {
      id: flight.id,
      flight_number: flight.flight_number || '',
      from_airport: flight.from_airport || '',
      to_airport: flight.to_airport || '',
      date_of_flight: flight.date_of_flight || ''
    };
    localStorage.setItem(CREATED_FLIGHT_PAYLOADS_KEY, JSON.stringify(cache));
  } catch {
    // Ignore cache failures; the backend record is still authoritative.
  }
};

const FlyAircraftModal = ({
  onClose,
  onSuccess,
  bookedFlights,
  bookedFlightsLoading = false,
  bookedFlightsError = '',
  aircraftLocked = false,
  aircraftLockChecking = false,
  aircraftLockedReason = ''
}) => {
  const { aircraft } = useAuth();
  const flightDropRef = useRef(null);
  const flightSearchRef = useRef(null);
  const lastBookedFlightsErrorRef = useRef('');
  const now = new Date();
  const usesPreloadedFlights = Array.isArray(bookedFlights);
  const [form, setForm] = useState({
    date: format(now, 'dd.MMM.yyyy HH:mm'),
    date_iso: now.toISOString(),
    flight_number: '',
    from_airport: '',
    to_airport: '',
  });
  const [upcomingFlights, setUpcomingFlights] = useState(usesPreloadedFlights ? bookedFlights : []);
  const [loading, setLoading] = useState(usesPreloadedFlights ? bookedFlightsLoading : false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [flightDropOpen, setFlightDropOpen] = useState(false);
  const [flightSearch, setFlightSearch] = useState('');
  const [selectedFlightLabel, setSelectedFlightLabel] = useState('');

  const normalizeFlightNumberInput = value =>
    value.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9]/g, '');
  const normalizeAirportInput = value =>
    value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);

  const isFlightNumberValid = FLIGHT_NUMBER_PATTERN.test(form.flight_number);
  const isFromAirportValid = ICAO_PATTERN.test(form.from_airport);
  const isToAirportValid = ICAO_PATTERN.test(form.to_airport);
  const hasValidRoute = isFromAirportValid && isToAirportValid;
  const canSubmit = !aircraftLocked && isFlightNumberValid && hasValidRoute;
  const getFlightOptionLabel = flight => {
    const flightNumber = flight.flight_number || 'Flight';
    const from = flight.from_label || flight.from_airport || '----';
    const to = flight.to_label || flight.to_airport || '----';
    return `${flightNumber} - ${from} - ${to}`;
  };
  const filteredFlights = upcomingFlights.filter(flight => {
    const term = flightSearch.trim().toLowerCase();
    if (!term) return true;
    return getFlightOptionLabel(flight).toLowerCase().includes(term);
  });

  useEffect(() => {
    if (!usesPreloadedFlights) return;
    setUpcomingFlights(bookedFlights || []);
    setLoading(bookedFlightsLoading);
    if (bookedFlightsError) {
      lastBookedFlightsErrorRef.current = bookedFlightsError;
      setError(bookedFlightsError);
    } else if (lastBookedFlightsErrorRef.current) {
      const previousBookedFlightsError = lastBookedFlightsErrorRef.current;
      lastBookedFlightsErrorRef.current = '';
      setError(current => current === previousBookedFlightsError ? '' : current);
    }
  }, [usesPreloadedFlights, bookedFlights, bookedFlightsLoading, bookedFlightsError]);

  useEffect(() => {
    if (usesPreloadedFlights || !aircraft) return;
    setLoading(true);
    getUpcomingFlights(aircraft.id)
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load booked flights');
        return { data: { flights: [] } };
      })
      .then((flightsRes) => {
      setUpcomingFlights(flightsRes.data.flights || []);
      })
      .finally(() => setLoading(false));
  }, [aircraft, usesPreloadedFlights]);

  useEffect(() => {
    const handler = e => {
      if (flightDropRef.current && !flightDropRef.current.contains(e.target)) {
        setFlightDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') setFlightDropOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!flightDropOpen) return;
    flightSearchRef.current?.focus();
  }, [flightDropOpen]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'flight_number') {
      setForm(f => ({ ...f, [name]: normalizeFlightNumberInput(value) }));
      setSelectedFlightLabel('');
    } else if (name === 'from_airport' || name === 'to_airport') {
      setForm(f => ({ ...f, [name]: normalizeAirportInput(value) }));
      setSelectedFlightLabel('');
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    setError('');
  };

  const handleSelectFlight = flight => {
    const selectedDate = flight.departure_time ? new Date(flight.departure_time) : null;
    const hasValidDate = selectedDate && !Number.isNaN(selectedDate.getTime());

    setForm(f => ({
      ...f,
      date: hasValidDate ? format(selectedDate, 'dd.MMM.yyyy HH:mm') : f.date,
      date_iso: hasValidDate ? selectedDate.toISOString() : f.date_iso,
      flight_number: normalizeFlightNumberInput(flight.flight_number || ''),
      from_airport: normalizeAirportInput(flight.from_airport || ''),
      to_airport: normalizeAirportInput(flight.to_airport || ''),
    }));
    setSelectedFlightLabel(getFlightOptionLabel(flight));
    setError('');
    setFlightDropOpen(false);
    setFlightSearch('');
  };

  const handleSubmit = async () => {
    if (aircraftLockChecking) {
      return;
    }
    if (aircraftLocked) {
      setError(aircraftLockedReason || 'Aircraft is currently flying');
      return;
    }
    if (!isFlightNumberValid) {
      setError('Flight number must be numeric or start with WZZ/WMT/WUK/W6/W4/W9');
      return;
    }
    if (!isFromAirportValid) {
      setError('Departure airport ICAO must be 4 letters');
      return;
    }
    if (!isToAirportValid) {
      setError('Arrival airport ICAO must be 4 letters');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await createFlight({
        aircraft_id: aircraft.id,
        flight_number: form.flight_number.trim().toUpperCase(),
        from_airport: form.from_airport.toUpperCase(),
        to_airport: form.to_airport.toUpperCase(),
        date_of_flight: form.date_iso || new Date().toISOString()
      });
      const createdFlight = {
        ...(res.data.flight || {}),
        flight_number: form.flight_number.trim().toUpperCase(),
        from_airport: form.from_airport.toUpperCase(),
        to_airport: form.to_airport.toUpperCase(),
        date_of_flight: form.date_iso || new Date().toISOString()
      };
      saveCreatedFlightPayload(createdFlight);
      onSuccess(createdFlight);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start flight');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="fly-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="fly-modal-header">
          <span>Fly Aircraft</span>
          <button className="fly-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="fly-modal-body">
          {(error || aircraftLockedReason) && <div className="fly-error">{error || aircraftLockedReason}</div>}
          
          {/* Inner Card Container */}
          <div className="fly-inner-card">
            
            {/* Date Row */}
            <div className="fly-row">
              <label className="fly-label">Date</label>
              <div className="fly-input-wrapper fly-input-readonly">
                <input value={form.date} readOnly />
              </div>
            </div>

            {/* Select Flight Row */}
            <div className="fly-row">
              <label className="fly-label">Select Flight</label>
              <div className="fly-flight-dropdown-wrap" ref={flightDropRef}>
                <button
                  type="button"
                  className="fly-flight-trigger"
                  disabled={loading || upcomingFlights.length === 0}
                  onClick={() => {
                    if (loading || upcomingFlights.length === 0) return;
                    setFlightDropOpen(v => !v);
                    if (flightDropOpen) setFlightSearch('');
                  }}
                >
                  <span className="fly-flight-trigger-text">
                    {loading ? 'Loading...' : upcomingFlights.length === 0
                      ? 'No booked flights'
                      : selectedFlightLabel || 'Quick select from booked flights'}
                  </span>
                  <span className={`fly-flight-chevron ${flightDropOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
                </button>

                <div className={`fly-flight-dropdown ${flightDropOpen ? 'open' : ''}`}>
                  <div className="fly-flight-search-wrap">
                    <input
                      ref={flightSearchRef}
                      className="fly-flight-search"
                      value={flightSearch}
                      onChange={e => setFlightSearch(e.target.value)}
                      placeholder="Search flight or route"
                    />
                  </div>

                  <div className="fly-flight-list">
                    {filteredFlights.map(flight => (
                      <button
                        type="button"
                        key={flight.id || `${flight.flight_number}-${flight.from_airport}-${flight.to_airport}`}
                        className="fly-flight-option"
                        onClick={() => handleSelectFlight(flight)}
                      >
                        <span className="fly-flight-option-main">{flight.flight_number || 'Flight'}</span>
                        <span className="fly-flight-option-route">
                          {flight.from_label || flight.from_airport || '----'} - {flight.to_label || flight.to_airport || '----'}
                        </span>
                      </button>
                    ))}
                    {filteredFlights.length === 0 && (
                      <div className="fly-flight-empty">No matching flights</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Flight Number Row */}
            <div className="fly-row">
              <label className="fly-label">Flight Number</label>
              <div className={`fly-input-wrapper fly-required-mark ${isFlightNumberValid ? 'complete' : 'incomplete'}`}>
                <input 
                  name="flight_number" 
                  autoFocus
                  value={form.flight_number} 
                  onChange={handleChange} 
                  autoComplete="off"
                  maxLength={8}
                />
              </div>
            </div>
            <div className="fly-row fly-row-hint">
              <div />
            </div>

            {/* From - To Row */}
            <div className="fly-row">
              <label className="fly-label">From — To</label>
              <div className="fly-split-row">
                
                {/* From Airport */}
                <div className={`fly-input-wrapper fly-required-mark fly-split-input ${isFromAirportValid ? 'complete' : 'incomplete'}`}>
                  <input
                    name="from_airport"
                    value={form.from_airport}
                    onChange={handleChange}
                    autoComplete="off"
                    maxLength={4}
                  />
                </div>

                <span className="fly-split-separator">–</span>

                {/* To Airport */}
                <div className={`fly-input-wrapper fly-required-mark fly-split-input ${isToAirportValid ? 'complete' : 'incomplete'}`}>
                  <input
                    name="to_airport"
                    value={form.to_airport}
                    onChange={handleChange}
                    autoComplete="off"
                    maxLength={4}
                  />
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="fly-modal-footer">
          <button className="fly-confirm-btn" onClick={handleSubmit} disabled={saving || !canSubmit}>
            {saving ? (
              'Starting...'
            ) : (
              <>
                <span className="fly-check-icon"><img src={checkmarkIcon} alt="" className="fly-icon-img app-icon app-icon-checkmark" /></span> Fly Aircraft
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlyAircraftModal;
